import { componerIdTpv } from "../utils/idTpv";
import dayjs from "dayjs";
import db from "../db/database";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import { markForSync } from "./syncService";
import { adjustLocalStock } from "./productService";
import { matchesDocumentType } from "../constants/documentType";
import {
  abonoRechazadoPorExceso,
  ESTADO_DE_LA_DEVOLUCION,
  estadoDeLaDevolucion,
  loQueQuedaPorProducto,
} from "../components/Invoice/loQueQuedaPorAbonar";

// Función para obtener el siguiente número de factura
export const getNextInvoiceNumber = async () => {
  try {
    // Obtener todas las facturas para encontrar el número más alto
    const allInvoices = await db.invoices.toArray();

    if (allInvoices.length === 0) {
      // Si no hay facturas, empezar desde F00001
      return "F00001";
    }

    // Filtrar facturas con formato válido (que empiecen con F seguido de números)
    const validNumbers = allInvoices
      .map((invoice) => invoice.No)
      .filter((no) => no && /^F\d+$/.test(no))
      .map((no) => parseInt(no.substring(1), 10));

    if (validNumbers.length === 0) {
      return "F00001";
    }

    // Encontrar el número máximo y agregar 1
    const maxNumber = Math.max(...validNumbers);
    const nextNumber = maxNumber + 1;

    // Formatear el número con ceros a la izquierda (F00001, F00002, etc.)
    return `F${nextNumber.toString().padStart(5, "0")}`;
  } catch (error) {
    console.error("Error al generar el siguiente número de factura:", error);
    // En caso de error, generar un número basado en la fecha y hora actual
    const timestamp = Date.now().toString().slice(-6);
    return `F${timestamp}`;
  }
};

// Funciones para gestionar facturas
export const addInvoice = async (invoice) => {
  try {
    // Calcular el descuento máximo de formas de pago si no viene en la factura
    let dto = invoice.Dto || 0;
    let importeDto = invoice.importeDto || 0;

    // El Id_TPV se escribe DENTRO de la misma transacción que el alta.
    //
    // Antes eran dos escrituras: un add que devolvía el id y, después, un
    // update con el identificador. Entre las dos, la factura existía sin él --y
    // si la sincronización en segundo plano la tomaba en esa ventana, viajaba a
    // Business Central sin identificar. Medido contra el ambiente: una de cada
    // cuatro facturas sin contabilizar no lo llevaba.
    //
    // El id local sólo existe después del add, así que la transacción es lo que
    // cierra la ventana: nadie observa la fila a medias.
    // Un solo reloj para toda la fila. Antes se leía dos veces --una para la
    // hora del cajero y otra para la marca de modificación-- y a las 23:59:59
    // eso deja un documento cuya hora es de un día y cuya marca es del
    // siguiente, justo contra el acuerdo de que cada caja usa su propia fecha.
    const ahora = dayjs();

    const { id, Id_TPV } = await db.transaction("rw", db.invoices, async () => {
      const idNuevo = await db.invoices.add({
        Document_Type: invoice.Document_Type || "Invoice",
        Posting_Date: invoice.Posting_Date,
        Document_Date: invoice.Document_Date,
        Sell_to_Customer_No_: invoice.Sell_to_Customer_No_,
        Sell_to_Customer_Name_2: invoice.Sell_to_Customer_Name_2 || "",
        Razon_Social: invoice.Razon_Social || "",
        Salesperson_Code: invoice.Salesperson_Code || "",
        // Campo para guardar el ID del cliente cuando aún no tiene No
        clientLocalId:
          typeof invoice.Sell_to_Customer_No_ === "number" ? invoice.Sell_to_Customer_No_ : null,
        // Datos de facturación
        Bill_to_Name: invoice.Bill_to_Name,
        Bill_to_Address: invoice.Bill_to_Address,
        Direccion_2: invoice.Direccion_2 || "",
        Bill_to_City: invoice.Bill_to_City || "",
        VAT_Registration_No_: invoice.VAT_Registration_No_ || "",
        Bill_to_Country_Region_Code: invoice.Bill_to_Country_Region_Code || "",
        // La ubicación del comprador, congelada al elegir al cliente. Sólo Costa
        // Rica la llena: ver `costarica/ubicacionDelReceptor.js`.
        Sell_to_Post_Code: invoice.Sell_to_Post_Code || "",
        Sell_to_Country_Region_Code: invoice.Sell_to_Country_Region_Code || "",
        E_Mail: invoice.E_Mail || "",
        Telefono: invoice.Telefono || "",
        Mobil: invoice.Mobil || "",
        // Código de dirección de envío
        Ship_to_Code: invoice.Ship_to_Code || "",
        // Tipo de documento de identificación fiscal
        RTN: invoice.RTN,
        Tipo_Documento_SrI: invoice.Tipo_Documento_SrI || null,
        Fiscal_Invoice_Type: invoice.Fiscal_Invoice_Type || "Factura consumidor final",
        // Tiquete o factura, congelado en el alta: un reintento del mismo
        // documento manda siempre lo mismo. `null` cuando no se pudo decidir
        // --el pais no distingue, o no se sabe el cliente por defecto de la
        // tienda--, y entonces la clave no viaja y BC aplica su respaldo.
        Electronic_Document_Type: invoice.Electronic_Document_Type || null,
        // Quien atendio la venta: el `No` del usuario de la sesion, que es la
        // misma convencion de los 40.092 documentos de 2019 a 2021. Nunca el
        // nombre y nunca la tienda --la tienda ya viaja en su propio campo--.
        ID_Cajero: invoice.ID_Cajero || "",
        NCR: invoice.NCR || "",
        Colegio: invoice.Colegio || null,
        // Datos para abonos
        Applies_to_Doc_Type: invoice.Applies_to_Doc_Type,
        Applies_to_Doc_No: invoice.Applies_to_Doc_No,
        // Motivo y sustento de NC (Perú)
        Cod_Tipo_Nota_Credito: invoice.Cod_Tipo_Nota_Credito || null,
        Tipo_Nota_Credito: invoice.Tipo_Nota_Credito || null,
        Sustento_Nota_Credito: invoice.Sustento_Nota_Credito || null,
        // Datos de caja y turno
        caja: invoice.caja || null,
        turno: invoice.turno || null,
        // Campos de descuento
        Dto: dto,
        importeDto: importeDto,
        // Campos de cupón
        Cupon: invoice.Cupon || null,
        Tipo_Detalle: invoice.Tipo_Detalle || null,
        No_Detalle: invoice.No_Detalle || null,
        // Campo de campaña
        Campaign_No_: invoice.Campaign_No_ || null,
        // Campos adicionales para Perú (Alumno)
        Student_Grade: invoice.Student_Grade || null,
        Student_Name: invoice.Student_Name || null,
        Student_DNI: invoice.Student_DNI || null,
        Student_Phone: invoice.Student_Phone || null,
        // Estado de borrador (por defecto no es borrador)
        isDraft: invoice.isDraft || false,
        Pre_Assigned_No:
          invoice.Document_Type === "Invoice" || invoice.Document_Type === "Boleta"
            ? invoice.Pre_Assigned_No
            : "",
        // La hora a la que el cajero dio de alta el documento.
        //
        // No se guardaba ninguna, y Business Central estampaba la de su propio
        // reloj --que va en UTC-- mientras la hora de apertura del turno viaja
        // en hora local: el rango del cierre y los documentos que caen dentro
        // se medían con dos relojes separados por cinco horas en Ecuador.
        //
        // Es la hora del ALTA, no la del envío: una venta hecha sin red a las
        // 21:42 y sincronizada a las 09:00 del día siguiente llegaría con las
        // 09:00 si se compusiera al mandarla. Por eso se escribe acá, dentro de
        // la misma transacción que el identificador -- acuerdo del recorrido de
        // la caja 3, §1.
        Hora_creacion: invoice.Hora_creacion || ahora.format("HH:mm:ss"),
        pendingSync: 1,
        lastModified: ahora.toDate(),
        deleted: false,
      });

      const idTpv = componerIdTpv(invoice.caja, idNuevo);
      await db.invoices.update(idNuevo, { Id_TPV: idTpv });

      return { id: idNuevo, Id_TPV: idTpv };
    });

    return { ...invoice, id, Id_TPV };
  } catch (error) {
    console.error("Error al crear factura:", error);
    throw error;
  }
};

// Función para guardar una factura como borrador
export const saveInvoiceAsDraft = async (invoice) => {
  try {
    // Asegurarnos de que se guarda como borrador
    const draftInvoice = {
      ...invoice,
      isDraft: true,
    };

    // Utilizar la función addInvoice existente con la marca de borrador
    return await addInvoice(draftInvoice);
  } catch (error) {
    console.error("Error al guardar factura como borrador:", error);
    throw error;
  }
};

// Función para convertir un borrador en factura normal
export const convertDraftToInvoice = async (id) => {
  try {
    // Verificar que la factura existe
    const invoice = await getInvoiceById(id);
    if (!invoice || invoice.deleted) {
      throw new Error("Factura no encontrada");
    }

    // Verificar que la factura es un borrador
    if (!invoice.isDraft) {
      throw new Error("La factura no es un borrador");
    }

    // Actualizar el estado de borrador
    await db.invoices.update(id, {
      isDraft: false,
      lastModified: new Date(),
      pendingSync: 1,
    });

    // Marcar como pendiente de sincronización
    await markForSync("invoices", id);

    return await getInvoiceById(id);
  } catch (error) {
    console.error(`Error al convertir borrador a factura con ID ${id}:`, error);
    throw error;
  }
};

export const getAllInvoices = async () => {
  try {
    return await db.invoices.filter((invoice) => !invoice.deleted).toArray();
  } catch (error) {
    console.error("Error al obtener facturas:", error);
    throw error;
  }
};

// Vista previa rápida (para pintar la pantalla al instante en negocios con
// mucho historial): `.filter().limit(n)` corta el escaneo en cuanto encuentra
// `n` coincidencias, en vez de recorrer toda la tabla de facturas.
export const getInvoicesPreview = async (documentType, limit = 50, countryCode) => {
  try {
    return await db.invoices
      .filter(
        (invoice) =>
          !invoice.deleted && matchesDocumentType(invoice.Document_Type, documentType, countryCode)
      )
      .limit(limit)
      .toArray();
  } catch (error) {
    console.error("Error al obtener vista previa de facturas:", error);
    throw error;
  }
};

// Función para obtener todos los borradores
export const getAllDrafts = async () => {
  try {
    return await db.invoices
      .filter((invoice) => !invoice.deleted && invoice.isDraft === true)
      .toArray();
  } catch (error) {
    console.error("Error al obtener borradores:", error);
    throw error;
  }
};

// Función para obtener todas las facturas que no son borradores
export const getAllNonDraftInvoices = async () => {
  try {
    return await db.invoices.filter((invoice) => !invoice.deleted && !invoice.isDraft).toArray();
  } catch (error) {
    console.error("Error al obtener facturas no borrador:", error);
    throw error;
  }
};

export const getInvoiceById = async (id) => {
  try {
    // Convertir ID a número si es necesario
    const numericId = parseInt(id, 10);
    if (isNaN(numericId)) {
      throw new Error("ID de factura no es un número válido");
    }

    return await db.invoices.get(numericId);
  } catch (error) {
    console.error(`Error al obtener factura con ID ${id}:`, error);
    throw error;
  }
};

export const getInvoiceByNo = async (No, fieldName = "No") => {
  try {
    return await db.invoices
      .where(fieldName)
      .equals(No)
      .and((invoice) => !invoice.deleted)
      .first();
  } catch (error) {
    console.error(`Error al obtener factura con No ${No}:`, error);
    throw error;
  }
};

export const updateInvoice = async (id, invoiceData) => {
  try {
    const invoice = await db.invoices.get(id);
    if (!invoice || invoice.deleted) {
      throw new Error("Factura no encontrada");
    }

    if (invoice.No) {
      throw new Error(
        "No se puede modificar una factura que ya ha sido sincronizada con Business Central"
      );
    }

    // Verificar si Sell_to_Customer_No_ es un ID local o un No
    // Preservar clientLocalId existente salvo que nos envíen explícitamente un número
    let clientLocalId = invoice.clientLocalId || null;
    if (
      invoiceData.Sell_to_Customer_No_ !== undefined &&
      typeof invoiceData.Sell_to_Customer_No_ === "number"
    ) {
      clientLocalId = invoiceData.Sell_to_Customer_No_;
    }

    // Asegurarnos de que los campos caja y turno se incluyen en la actualización
    const updatedData = {
      ...invoiceData,
      Id_TPV: invoiceData.Id_TPV !== undefined ? invoiceData.Id_TPV : invoice.Id_TPV || "",
      clientLocalId,
      // Asegurar que los campos de descuento están incluidos
      Dto: invoiceData.Dto !== undefined ? invoiceData.Dto : invoice.Dto,
      importeDto:
        invoiceData.importeDto !== undefined ? invoiceData.importeDto : invoice.importeDto,
      // Asegurar que el campo cupón está incluido
      Cupon: invoiceData.Cupon !== undefined ? invoiceData.Cupon : invoice.Cupon,
      Tipo_Detalle:
        invoiceData.Tipo_Detalle !== undefined ? invoiceData.Tipo_Detalle : invoice.Tipo_Detalle,
      No_Detalle:
        invoiceData.No_Detalle !== undefined ? invoiceData.No_Detalle : invoice.No_Detalle,
      // Asegurar que el campo de campaña está incluido
      Campaign_No_:
        invoiceData.Campaign_No_ !== undefined ? invoiceData.Campaign_No_ : invoice.Campaign_No_,
      // Asegurar que el campo de tipo de documento está incluido
      Tipo_Documento_SrI:
        invoiceData.Tipo_Documento_SrI !== undefined
          ? invoiceData.Tipo_Documento_SrI
          : invoice.Tipo_Documento_SrI || null,
      Fiscal_Invoice_Type:
        invoiceData.Fiscal_Invoice_Type !== undefined
          ? invoiceData.Fiscal_Invoice_Type
          : invoice.Fiscal_Invoice_Type || "Factura consumidor final",
      NCR: invoiceData.NCR !== undefined ? invoiceData.NCR : invoice.NCR || "",
      lastModified: new Date(),
      pendingSync: 1,
    };

    // Actualizar la factura
    await db.invoices.update(id, updatedData);

    // Marcar como pendiente de sincronización
    await markForSync("invoices", id);

    return await getInvoiceById(id);
  } catch (error) {
    console.error(`Error al actualizar factura con ID ${id}:`, error);
    throw error;
  }
};

export const deleteInvoice = async (id) => {
  try {
    // Verificar que el ID sea válido
    if (!id && id !== 0) {
      throw new Error("ID de factura inválido");
    }

    // Convertir ID a número si es necesario
    const numericId = parseInt(id, 10);
    if (isNaN(numericId)) {
      throw new Error("ID de factura no es un número válido");
    }

    // Verificar primero si la factura existe y no está sincronizada
    const existingInvoice = await getInvoiceById(numericId);

    if (!existingInvoice) {
      throw new Error("La factura no existe");
    }

    // No permitir eliminar si ya tiene número de BC
    if (existingInvoice.No) {
      throw new Error(
        "No se puede eliminar una factura que ya ha sido sincronizada con Business Central"
      );
    }

    const now = getCurrentDateTimeISO();

    // Marcar como eliminado (soft delete)
    const updateCount = await db.invoices.update(numericId, {
      deleted: true,
      pendingSync: 1,
      lastModified: now,
    });

    if (updateCount === 0) {
      throw new Error("No se pudo marcar la factura como eliminada");
    }

    // También marcar las líneas asociadas como eliminadas
    const invoiceLines = await db.invoiceLines.where("invoiceId").equals(numericId).toArray();

    for (const line of invoiceLines) {
      if (line.deleted) continue;

      await db.invoiceLines.update(line.id, {
        deleted: true,
        pendingSync: 1,
        lastModified: now,
      });

      // Se está eliminando la factura entera (ej. un borrador) -- revertir
      // el stock que se había descontado por cada una de sus líneas.
      await adjustLocalStock(line.No, line.locationCode, -(line.Quantity || 0));
    }

    return true;
  } catch (error) {
    console.error(`Error al eliminar factura con ID ${id}:`, error);
    throw error;
  }
};

// Funciones adicionales para trabajar con las líneas de factura

export const addInvoiceLine = async (invoiceLine) => {
  try {
    // Verificar primero si la factura existe y no está sincronizada
    const parentInvoice = await getInvoiceById(invoiceLine.invoiceId);
    if (!parentInvoice) {
      throw new Error("La factura no existe");
    }

    // No permitir añadir líneas si la factura ya está sincronizada
    if (parentInvoice.No) {
      throw new Error(
        "No se pueden añadir líneas a una factura que ya ha sido sincronizada con Business Central"
      );
    }

    const now = getCurrentDateTimeISO();
    const lineWithMeta = {
      ...invoiceLine,
      pendingSync: 1,
      lastModified: now,
      lastSynced: null,
      deleted: false,
    };

    const id = await db.invoiceLines.add(lineWithMeta);

    // El stock local recién se toca acá, cuando la línea ya está creada en
    // IndexedDB (no hace falta que esté sincronizada) -- no antes, mientras
    // la línea sólo existe en memoria (ver InvoiceLines.js/_isLocalLine).
    await adjustLocalStock(lineWithMeta.No, lineWithMeta.locationCode, lineWithMeta.Quantity);

    return { ...lineWithMeta, id };
  } catch (error) {
    console.error("Error al agregar línea de factura:", error);
    throw error;
  }
};

export const getInvoiceLines = async (invoiceId) => {
  try {
    return await db.invoiceLines
      .where("invoiceId")
      .equals(invoiceId)
      .and((line) => !line.deleted)
      .toArray();
  } catch (error) {
    console.error(`Error al obtener líneas de factura para ID ${invoiceId}:`, error);
    throw error;
  }
};

export const updateInvoiceLine = async (id, updatedLine) => {
  try {
    // Obtener la línea actual
    const existingLine = await db.invoiceLines.get(id);
    if (!existingLine) {
      throw new Error("La línea de factura no existe");
    }

    // Verificar si la factura padre está sincronizada
    const parentInvoice = await getInvoiceById(existingLine.invoiceId);
    if (!parentInvoice) {
      throw new Error("La factura no existe");
    }

    // No permitir actualizar si la factura ya está sincronizada
    if (parentInvoice.No) {
      throw new Error(
        "No se pueden modificar líneas de una factura que ya ha sido sincronizada con Business Central"
      );
    }

    const now = getCurrentDateTimeISO();

    const lineToUpdate = {
      ...updatedLine,
      pendingSync: 1,
      lastModified: now,
    };

    await db.invoiceLines.update(id, lineToUpdate);

    // Ajustar stock local por la diferencia de cantidad (positiva o
    // negativa) - ver nota en addInvoiceLine.
    const quantityDelta = (lineToUpdate.Quantity || 0) - (existingLine.Quantity || 0);
    await adjustLocalStock(
      existingLine.No,
      existingLine.locationCode || lineToUpdate.locationCode,
      quantityDelta
    );

    return { ...lineToUpdate, id };
  } catch (error) {
    console.error(`Error al actualizar línea de factura con ID ${id}:`, error);
    throw error;
  }
};

export const deleteInvoiceLine = async (id) => {
  try {
    // Obtener la línea actual
    const existingLine = await db.invoiceLines.get(id);
    if (!existingLine) {
      throw new Error("La línea de factura no existe");
    }

    // Verificar si la factura padre está sincronizada
    const parentInvoice = await getInvoiceById(existingLine.invoiceId);
    if (!parentInvoice) {
      throw new Error("La factura no existe");
    }

    // No permitir eliminar si la factura ya está sincronizada
    if (parentInvoice.No) {
      throw new Error(
        "No se pueden eliminar líneas de una factura que ya ha sido sincronizada con Business Central"
      );
    }

    const now = getCurrentDateTimeISO();

    await db.invoiceLines.update(id, {
      deleted: true,
      pendingSync: 1,
      lastModified: now,
    });

    // Revertir el stock que se había descontado al crear/actualizar esta
    // línea - finalmente no se vendió.
    await adjustLocalStock(
      existingLine.No,
      existingLine.locationCode,
      -(existingLine.Quantity || 0)
    );

    return true;
  } catch (error) {
    console.error(`Error al eliminar línea de factura con ID ${id}:`, error);
    throw error;
  }
};

// Función para obtener factura completa con sus líneas
export const getCompleteInvoice = async (invoiceId) => {
  try {
    const invoice = await getInvoiceById(invoiceId);
    if (!invoice) return null;

    let lines = [];
    // priorizar No (document number final en BC); fallback a Pre_Assigned_No si aún no está contabilizado
    //
    // Hay un comentario en `getFinalDocumentNo` que dice que «las Credit Memo
    // siempre cuentan con Pre_Assigned_No de la factura relacionada». **Es
    // falso, y llevó a buscar acá un defecto que no existe**: si eso fuera
    // cierto, un abono pediría sus líneas por el número de su factura y se
    // llevaría las de ella.
    //
    // Medido el 11/09/2026 sobre 228 abonos contabilizados --199 en Ecuador
    // TEST y 29 en Costa Rica producción--: **todos** llevan su propio número
    // de borrador, con la serie de abonos (VNC/VN), y **ninguno** el de la
    // factura que corrige. El terminal tampoco lo hereda: `InvoiceManagement`
    // pone `Pre_Assigned_No: ""` al crear el abono, y la sincronización escribe
    // después el que devuelve Business Central, que es el suyo.
    //
    // Así que este respaldo vale igual para los dos tipos de documento. Queda
    // anotado para que el comentario equivocado no vuelva a mandar a nadie por
    // el mismo camino.
    const invoiceNoToUse = invoice.No || invoice.Pre_Assigned_No;

    if (invoiceNoToUse) {
      lines = await db.invoiceLines
        .where("invoiceNo")
        .equals(invoiceNoToUse)
        .and((l) => !l.deleted)
        .toArray();
    }
    if (lines.length === 0) {
      // local invoice sin sincronizar → usa autoincremental Dexie id
      lines = await db.invoiceLines
        .where("invoiceId")
        .equals(invoice.id)
        .and((l) => !l.deleted)
        .toArray();
    }

    return { ...invoice, lines };
  } catch (error) {
    console.error(`Error al obtener factura completa con ID ${invoiceId}:`, error);
    throw error;
  }
};

// Función para obtener las facturas del día actual
export const getTodaysInvoices = async () => {
  try {
    const today = dayjs().format("YYYY-MM-DD");
    const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");

    // Posting_Date está indexado (ver schemaV11 en db/database.js) -- antes
    // se traía toda la tabla y se filtraba en JS, mismo problema ya resuelto
    // para getInvoicesByDateRange.
    return await db.invoices
      .where("Posting_Date")
      .between(today, tomorrow, true, false)
      .and((invoice) => !invoice.deleted)
      .toArray();
  } catch (error) {
    console.error("Error al obtener facturas del día:", error);
    throw error;
  }
};

// Función para obtener facturas en un rango de fecha y hora específico
// (útil para cierres de caja con múltiples turnos)
export const getInvoicesByDateTimeRange = async (startDateTime, endDateTime = null) => {
  try {
    // Si no se proporciona fecha de fin, usar la fecha y hora actual
    const endDate = endDateTime || new Date();

    // Convertir fechas a objetos dayjs para comparación
    const startDate = dayjs(startDateTime);
    const endDateFormatted = dayjs(endDate);

    return await db.invoices
      .filter((invoice) => {
        // Verificar que no esté eliminada ni sea borrador
        if (invoice.deleted || invoice.isDraft) return false;

        // Usar lastModified para determinar cuándo se creó/modificó la factura
        if (!invoice.lastModified) return false;

        const invoiceDateTime = dayjs(invoice.lastModified);

        // La factura debe estar dentro del rango de tiempo
        const isInRange =
          invoiceDateTime.isAfter(startDate) && invoiceDateTime.isBefore(endDateFormatted);

        return isInRange;
      })
      .toArray();
  } catch (error) {
    console.error("Error al obtener facturas por rango de fecha y hora:", error);
    throw error;
  }
};

// Función para obtener facturas por tipo
export const getInvoicesByType = async (documentType) => {
  try {
    return await db.invoices
      .filter((invoice) => !invoice.deleted && invoice.Document_Type === documentType)
      .toArray();
  } catch (error) {
    console.error(`Error al obtener facturas de tipo ${documentType}:`, error);
    throw error;
  }
};

// Función para obtener todos los abonos relacionados con una factura específica
//
// Deja fuera la nota de crédito que Business Central rechazó por `ABONO_EXCEDE`:
// no se registró, BC no la cuenta y no se va a reintentar, así que restarla
// dejaba sin disponible justo la nota de crédito que el aviso pide hacer.
// Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H2 · TPV.
export const getCreditMemosForInvoice = async (invoiceNo) => {
  try {
    return await db.invoices
      .where("Applies_to_Doc_No")
      .equals(invoiceNo)
      .and(
        (invoice) =>
          !invoice.deleted &&
          invoice.Document_Type === "Credit Memo" &&
          !abonoRechazadoPorExceso(invoice)
      )
      .toArray();
  } catch (error) {
    console.error(`Error al obtener las notas de crédito para la factura ${invoiceNo}:`, error);
    throw error;
  }
};

// Reúne las líneas de todas las notas de crédito de una factura y pregunta
// cuánto queda por devolver de cada producto. La cuenta en sí --agrupar por
// artículo, acumular las líneas repetidas, redondear cada paso-- vive en
// `loQueQuedaPorAbonar`, que es donde se puede probar sin base.
//
// excludeCreditMemoId: al validar o reabrir una NC que ya existe en Dexie
// (borrador), hay que excluir su propio id -- si no, se contaría a sí misma
// como "ya abonada" y el disponible calculado saldría de menos.
//
// country: la precisión del importe con IVA por producto, que es lo que usa la
// validación de la nota de crédito (H2 · TPV del 14/09). Quien sólo mira
// cantidades puede omitirlo.
export const getRemainingQuantitiesForInvoice = async (
  completeInvoice,
  { excludeCreditMemoId, country = null } = {}
) => {
  const lineasDeLosAbonos = [];

  if (completeInvoice?.No) {
    const allCreditMemos = await getCreditMemosForInvoice(completeInvoice.No);
    const existingCreditMemos = excludeCreditMemoId
      ? allCreditMemos.filter((cm) => cm.id !== excludeCreditMemoId)
      : allCreditMemos;

    for (const creditMemo of existingCreditMemos) {
      let creditMemoWithLines = creditMemo;
      if (!creditMemo.lines) {
        creditMemoWithLines = await getCompleteInvoice(creditMemo.id);
      }

      if (creditMemoWithLines.lines) {
        lineasDeLosAbonos.push(...creditMemoWithLines.lines);
      }
    }
  }

  // La cuenta en sí vive en `loQueQuedaPorAbonar`: es una regla pura, la
  // comparten el listado y este servicio, y así no puede haber dos versiones.
  return loQueQuedaPorProducto(completeInvoice?.lines, lineasDeLosAbonos, country);
};

// Recalcula y guarda en la factura original en qué punto está su devolución.
// Se llama solo en los puntos donde cambia el estado de las NC de esa factura
// (crear/guardar borrador/eliminar una NC) -- no en cada render del listado --
// para que InvoiceList pueda pintar el ícono de "Crear nota de crédito" con una
// simple lectura de campo, sin recalcular por cada fila cada vez que se pinta
// la lista.
//
// Son dos campos porque son dos cosas distintas para el cajero:
//   - `fullyReturned`: no queda nada por devolver, el ícono se apaga.
//   - `partiallyReturned`: ya se abonó algo y **todavía queda**. El ícono sigue
//     encendido, pero en otro color: antes esta factura se veía igual que una
//     intacta y no había forma de saber que ya tenía una nota de crédito.
export const refreshInvoiceReturnAvailability = async (originalInvoiceNo) => {
  if (!originalInvoiceNo) return;

  try {
    const originalInvoice = await getInvoiceByNo(originalInvoiceNo);
    if (!originalInvoice) return;

    const completeOriginalInvoice = await getCompleteInvoice(originalInvoice.id);
    const remainingQuantities = await getRemainingQuantitiesForInvoice(completeOriginalInvoice);

    const estado = estadoDeLaDevolucion(remainingQuantities);

    await db.invoices.update(originalInvoice.id, {
      fullyReturned: estado === ESTADO_DE_LA_DEVOLUCION.COMPLETA,
      partiallyReturned: estado === ESTADO_DE_LA_DEVOLUCION.PARCIAL,
    });
  } catch (error) {
    // No bloquear el guardado de la NC por esto -- es solo para
    // deshabilitar proactivamente el ícono, el control real sigue siendo
    // la validación en processInvoiceNo/handleSubmit.
    console.error(
      `Error al recalcular disponibilidad de devolución para la factura ${originalInvoiceNo}:`,
      error
    );
  }
};

// Función para obtener facturas del día por tipo
export const getTodaysInvoicesByType = async (documentType) => {
  try {
    const todaysInvoices = await getTodaysInvoices();
    return todaysInvoices.filter((invoice) => invoice.Document_Type === documentType);
  } catch (error) {
    console.error(`Error al obtener facturas de hoy de tipo ${documentType}:`, error);
    throw error;
  }
};

const isPendingSyncInvoice = (invoice) =>
  !invoice.deleted && !invoice.isDraft && (invoice.pendingSync || invoice.syncError);

/**
 * Cuántos documentos esperan viaje, para el aviso del menú.
 *
 * Cuenta con el **mismo predicado** que arma el listado de
 * /facturas-pendientes: si el número del menú y lo que se ve al entrar no
 * coincidieran, el aviso dejaría de creerse a la primera.
 *
 * Cuenta sin materializar la lista. `getPendingSyncInvoices` trae todas las
 * facturas a memoria y luego filtra --le hace falta, devuelve los documentos--,
 * y para un número al lado de un rótulo eso es caro: en el ambiente de pruebas
 * de Ecuador son 2.021 facturas, y en producción 72.386.
 */
export const contarPendientesDeSincronizar = async () => {
  try {
    return await db.invoices.filter(isPendingSyncInvoice).count();
  } catch (error) {
    console.error("Error al contar los documentos pendientes de sincronizar:", error);
    // Un cero es «no molesto con un aviso que no sé si es cierto». Devolver otra
    // cosa pondría un número inventado en el menú.
    return 0;
  }
};

/**
 * Facturas pendientes de sincronizar, alineado con el listado de /facturas-pendientes.
 * @param {{ dateFilter?: string, documentType?: string }} options
 */
export const getPendingSyncInvoices = async ({ dateFilter, documentType } = {}) => {
  try {
    let result;

    if (dateFilter === "today") {
      const todaysInvoices = await getTodaysInvoices();
      result = todaysInvoices.filter(isPendingSyncInvoice);
    } else {
      const allInvoices = await getAllInvoices();
      result = allInvoices.filter(isPendingSyncInvoice);
    }

    if (documentType) {
      result = result.filter((invoice) => invoice.Document_Type === documentType);
    }

    return result;
  } catch (error) {
    console.error("Error al obtener facturas pendientes de sincronizar:", error);
    throw error;
  }
};

export const getTodaysPendingInvoices = async () => {
  return getPendingSyncInvoices({ dateFilter: "today" });
};

// Función para actualizar el No de cliente en las facturas cuando el cliente se sincroniza
export const updateClientNoInInvoices = async (clientId, clientNo) => {
  try {
    // Buscar de forma robusta: por clientLocalId, por Sell_to_Customer_No_ num y string
    const byLocalId = await db.invoices.where("clientLocalId").equals(clientId).toArray();

    const bySellToNum = await db.invoices.where("Sell_to_Customer_No_").equals(clientId).toArray();

    const bySellToStr = await db.invoices
      .where("Sell_to_Customer_No_")
      .equals(String(clientId))
      .toArray();

    const mapById = new Map();
    [...byLocalId, ...bySellToNum, ...bySellToStr].forEach((inv) => {
      if (inv && !mapById.has(inv.id)) mapById.set(inv.id, inv);
    });
    const invoices = Array.from(mapById.values());

    // No hay facturas que actualizar
    if (invoices.length === 0) {
      return 0;
    }

    // Actualizar cada factura con el No del cliente
    for (const invoice of invoices) {
      await db.invoices.update(invoice.id, {
        Sell_to_Customer_No_: clientNo,
        clientLocalId: null, // Ya no necesitamos guardar el ID local
        lastModified: new Date(),
        pendingSync: 1,
      });

      // Marcar como pendiente de sincronización
      await markForSync("invoices", invoice.id);
    }

    return invoices.length;
  } catch (error) {
    console.error(`Error al actualizar No de cliente en facturas:`, error);
    throw error;
  }
};

export const getInvoicesByDateRange = async (start, end = "", turn, tpv, type = "Invoice") => {
  if (!end) {
    end = start;
  }

  // Posting_Date se guarda como string "YYYY-MM-DD" (a veces con hora
  // incluida) -- se usa el inicio del día siguiente a "end" como límite
  // superior EXCLUSIVO, igual que el filtro OData en modo BC
  // (`Posting_Date lt end`), para no descartar por comparación de texto un
  // documento del último día que traiga hora distinta de medianoche.
  const startKey = start;
  const endKey = dayjs(end).add(1, "day").format("YYYY-MM-DD");

  // Antes se traía TODA la tabla filtrada solo por Document_Type y se
  // filtraba la fecha en JS -- Posting_Date ahora está indexado (ver
  // schemaV11 en db/database.js), así que el rango de fechas es lo que
  // acota la consulta en IndexedDB en vez de traer todo el historial.
  const invoices = await db.invoices
    .where("Posting_Date")
    .between(startKey, endKey, true, false)
    .and((x) => x.Document_Type === type && !x.deleted)
    .toArray();

  return invoices.filter((inv) => (!turn || inv.turno === turn) && (!tpv || inv.caja === tpv));
};

export const getInvoicesLines = async (invoiceNos, type = "Invoice") => {
  const hasNumber = invoiceNos.some((x) => typeof x === "number");
  let invoices = await db.invoiceLines
    .where(!hasNumber ? "invoiceNo" : "invoiceId")
    .anyOf(invoiceNos)
    .and((l) => !l.deleted)
    .toArray();

  return invoices;
};
